import React from 'react';
import { Link } from 'react-router-dom';
import { Star, User, Award, ChevronRight } from 'lucide-react';
import testimonialData from '../data/testimonials.json';
import ScrollAnimation from './ScrollAnimation';

interface TestimonialType {
  id: number;
  name: string;
  role: string;
  rating: number;
  text: string;
  service: string;
  featured: boolean;
  approved?: boolean;
  flagged?: boolean;
  date?: string;
}

interface RecentTestimonialsProps {
  limit?: number;
  className?: string;
}

const RecentTestimonials: React.FC<RecentTestimonialsProps> = ({ 
  limit = 3,
  className = ''
}) => {
  // Get approved testimonials and sort by date (newest first)
  // If no date field exists on older testimonials, put them at the end
  const approvedTestimonials = (testimonialData.testimonials as TestimonialType[])
    .filter(t => t.approved !== false && t.flagged !== true)
    .sort((a, b) => {
      // If both have dates, sort newest first
      if (a.date && b.date) {
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      }
      // If only one has a date, prioritize the one with a date
      if (a.date) return -1;
      if (b.date) return 1;
      // If neither has a date, maintain current order
      return 0;
    })
    .slice(0, limit);

  if (approvedTestimonials.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      <ScrollAnimation>
        <h3 className="text-2xl font-bold mb-4 text-center">What Clients Say</h3>
      </ScrollAnimation>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {approvedTestimonials.map((testimonial, index) => (
          <ScrollAnimation key={testimonial.id} animation="fadeUp" delay={0.1 * index}>
            <div className="glassmorphic p-4 relative h-full flex flex-col">
              <div className="absolute top-4 right-4 flex">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} fill="#FFBA00" color="#FFBA00" size={16} />
                ))}
              </div>
              <div className="flex items-center mb-2">
                <div className="w-9 h-9 bg-gradient-to-br from-client-yellow to-client-teal rounded-full flex items-center justify-center mr-2">
                  <User className="text-black" size={14} />
                </div>
                <div>
                  <h4 className="font-bold text-sm">{testimonial.name}</h4>
                  <p className="text-xs text-white">{testimonial.role}</p>
                </div>
              </div>
              <p className="mb-3 text-sm text-white flex-grow">
                {testimonial.text.length > 120 
                  ? `${testimonial.text.substring(0, 120)}...` 
                  : testimonial.text}
              </p>
              <div className="flex items-center">
                <Award size={14} className="text-client-yellow mr-2" />
                <span className="text-xs text-client-yellow">{testimonial.service}</span>
              </div>
            </div>
          </ScrollAnimation>
        ))}
      </div>

      <ScrollAnimation animation="fadeUp" delay={0.3}>
        <div className="mt-6 text-center">
          <Link 
            to="/testimonials" 
            className="inline-flex items-center text-client-yellow hover:text-client-teal transition-colors duration-300"
          >
            View All Testimonials
            <ChevronRight size={16} className="ml-1" />
          </Link>
        </div>
      </ScrollAnimation>
    </div>
  );
};

export default RecentTestimonials; 